import { inboxCommonStyles } from '@/constants/themes';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { SnackBarEntry } from '@/types/appStates';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { setImapCredentials } from '@/utils/imapCredentials';
import { createTimeoutSignal } from '@/utils/signalConfig';
import { Delete, Visibility, VisibilityOff } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import type { SelectChangeEvent } from '@mui/material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import * as Papa from 'papaparse';
import { useCallback, useEffect, useState } from 'react';

interface ImapModalProps {
  open: boolean;
  onClose: (event?: {}, reason?: 'escapeKeyDown' | 'backdropClick') => void;
  setIsCredentialsSet: React.Dispatch<React.SetStateAction<boolean>>;
  setInboxAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ValidatedEmailCredential {
  imapEmail: string;
  imapPassword: string;
  meetingLink: string;
  provider: string;
  providerIMAPHost?: string;
  providerIMAPPort?: number;
  providerSMTPHost?: string;
  providerSMTPPort?: number;
  providerInboxName?: string;
  providerSentBoxName?: string;
}

interface ValidationResult {
  valid: boolean;
  errors: string[];
  data: ValidatedEmailCredential[];
}

interface EmailCredentialsCSVRow {
  emailId: string;
  password: string;
  meetingLink: string;
  provider?: string;
  providerIMAPHost?: string;
  providerIMAPPort?: string;
  providerSMTPHost?: string;
  providerSMTPPort?: string;
  providerInboxName?: string;
  providerSentBoxName?: string;
}

interface ExistingCredential {
  _id: string;
  emailId: string;
  password: string;
  meetingLink: string;
  provider: string;
  providerIMAPHost?: string;
  providerIMAPPort?: number;
  providerSMTPHost?: string;
  providerSMTPPort?: number;
  providerInboxName?: string;
  providerSentBoxName?: string;
}

function ImapModal({
  open,
  onClose,
  setIsCredentialsSet,
  setInboxAuthenticated,
}: ImapModalProps): React.ReactNode {
  const { inboxEmails, setStoreState, inbox, snackbarEntries } =
    useAlchemystStoreForAi((store) => {
      return {
        inboxEmails: store.inbox.inboxEmails,
        inbox: store.inbox,
        setStoreState: store.setStoreState,
        snackbarEntries: store.snackbarEntries,
      };
    });

  const [allowUploading, setAllowUploading] = useState(false);
  const [existingCredentials, setExistingCredentials] = useState<
    ExistingCredential[]
  >([]);
  const [loadingExistingCredentials, setLoadingExistingCredentials] =
    useState(false);
  const [emailSettings, setEmailSettings] = useState<{
    imapEmail: string;
    imapPassword: string;
    meetingLink: string;
    provider: string;
    providerIMAPHost: string;
    providerIMAPPort: number;
    providerSMTPHost: string;
    providerSMTPPort: number;
    providerInboxName: string;
    providerSentBoxName: string;
    loading: boolean;
    error: string;
  }>({
    imapEmail: '',
    imapPassword: '',
    meetingLink: '',
    provider: 'gmail',
    providerIMAPHost: '',
    providerIMAPPort: 0,
    providerSMTPPort: 0,
    providerSMTPHost: '',
    providerInboxName: '',
    providerSentBoxName: '',
    loading: false,
    error: '',
  });
  const [emailsAdded, setEmailsAdded] = useState<string[]>([]);
  const [credentials, setCredentials] = useState<
    ValidatedEmailCredential[] | null
  >(null);
  const [addyingInbox, setAddyingInbox] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const fetchEmails = async () => {
      try {
        const response = await fetchWithRewrites(
          '/api/v1/inbox/list?emailsOnly=true',
          {
            signal: createTimeoutSignal(),
          }
        );
        if (!response.ok) throw new Error('Failed to fetch emails');
        const emails = await response.json();
        if (emails.data.length > 0) {
          setStoreState({
            inbox: {
              ...inbox,
              inboxEmails: emails.data.map(
                (email: { emailId: string }) => email.emailId
              ),
            },
          });
        }
      } catch (error) {
        console.log('Error', error);
      }
    };
    fetchEmails();
  }, []);

  // Fetch existing credentials
  useEffect(() => {
    const fetchExistingCredentials = async () => {
      if (!open) return;

      setLoadingExistingCredentials(true);
      try {
        const response = await fetchWithRewrites('/api/v1/inbox/list', {
          signal: createTimeoutSignal(),
        });
        if (!response.ok)
          throw new Error('Failed to fetch existing credentials');
        const data = await response.json();
        setExistingCredentials(data.data || []);
      } catch (error) {
        console.log('Error fetching existing credentials:', error);
      } finally {
        setLoadingExistingCredentials(false);
      }
    };
    fetchExistingCredentials();
  }, [open]);

  /**
   * Validates the email and meeting link inputs.
   * @returns {boolean} - Returns true if inputs are valid, false otherwise.
   */
  const validateInputs = useCallback(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const urlRegex = /^(ftp|http|https):\/\/[^ "]+$/;
    if (!emailRegex.test(emailSettings.imapEmail)) {
      setEmailSettings((prev) => ({
        ...prev,
        error: 'Invalid email format',
      }));
      return false;
    }
    if (!urlRegex.test(emailSettings.meetingLink)) {
      setEmailSettings((prev) => ({
        ...prev,
        error: 'Invalid meeting link format',
      }));
      return false;
    }
    setEmailSettings((prev) => ({
      ...prev,
      error: '',
    }));
    return true;
  }, [emailSettings.imapEmail, emailSettings.meetingLink]);

  const handleInputChange =
    (field: Exclude<keyof typeof emailSettings, 'error' | 'loading'>) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmailSettings((prev) => ({
        ...prev,
        [field]: event.target.value,
        error: '', // Clear error when input is changed
      }));
    };

  const handleProviderChange = (event: SelectChangeEvent) => {
    setEmailSettings((prev) => ({
      ...prev,
      provider: event.target.value,
      error: '', // Clear error when input is changed
    }));
  };

  /**
   * Handles the save action by setting IMAP credentials.
   * Sets loading state during the process and updates authentication status.
   */
  const handleSave = async () => {
    if (!validateInputs()) return;
    setEmailSettings((prev) => ({
      ...prev,
      loading: true,
    }));
    try {
      const isSet = await setImapCredentials(
        emailSettings.imapEmail,
        emailSettings.imapPassword,
        emailSettings.meetingLink,
        emailSettings.provider,
        emailSettings.providerIMAPHost,
        emailSettings.providerIMAPPort,
        emailSettings.providerSMTPHost,
        emailSettings.providerSMTPPort,
        emailSettings.providerInboxName,
        emailSettings.providerSentBoxName
      );
      if (isSet) {
        setIsCredentialsSet(true);
        setInboxAuthenticated(true);
        onClose();

        //add to inbox email to store
        const newInboxEmails = [...inboxEmails, emailSettings.imapEmail];
        setStoreState({
          inbox: { ...inbox, inboxEmails: newInboxEmails },
        });
      } else
        setEmailSettings((prev) => ({
          ...prev,
          error: 'Failed to set IMAP credentials.',
        }));
    } finally {
      setEmailSettings((prev) => ({
        ...prev,
        imapEmail: '',
        imapPassword: '',
        meetingLink: '',
        provider: 'gmail',
        providerIMAPHost: '',
        providerIMAPPort: 0,
        providerSMTPHost: '',
        providerSMTPPort: 0,
        providerInboxName: '',
        providerSentBoxName: '',
        loading: false,
      }));
    }
  };

  const connectUsingMicrosoft = async () => {
    if (!validateInputs()) return;
    setEmailSettings((prev) => ({
      ...prev,
      loading: true,
    }));

    const { error, loading, ...rest } = emailSettings;

    try {
      const res = await fetchWithRewrites('/api/v1/inbox/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credentials: {
            email: rest.imapEmail,
            password: rest.imapPassword,
            meetingLink: rest.meetingLink,
            provider: rest.provider,
            providerIMAPHost: rest.providerIMAPHost,
            providerIMAPPort: rest.providerIMAPPort,
            providerSMTPHost: rest.providerSMTPHost,
            providerSMTPPort: rest.providerSMTPPort,
            providerInboxName: rest.providerInboxName,
            providerSentBoxName: rest.providerSentBoxName,
          },
        }),
      });

      if (res.ok) {
        const data = await res.json();
        // console.log('REDIRECT URL', data);

        // navigate(`${data.redirectUrl as string}`);
        setEmailSettings((prev) => ({
          ...prev,
          loading: false,
        }));
        onClose();
        window.open(data.redirectUrl as string, '_blank');
      }
    } catch (error) {
      console.error('Error', error);
    } finally {
      setEmailSettings((prev) => ({
        ...prev,
        imapEmail: '',
        imapPassword: '',
        meetingLink: '',
        provider: 'gmail',
        providerIMAPHost: '',
        providerIMAPPort: 0,
        providerSMTPHost: '',
        providerSMTPPort: 0,
        providerInboxName: '',
        providerSentBoxName: '',
        loading: false,
      }));
    }
  };

  function validateRow(
    row: EmailCredentialsCSVRow,
    rowIndex: number
  ): {
    valid: boolean;
    errors: string[];
    data?: ValidatedEmailCredential;
  } {
    const errors: string[] = [];

    if (!row.emailId) {
      errors.push(`Row ${rowIndex + 1}: field "emailId" is required`);
    }
    if (!row.password) {
      errors.push(`Row ${rowIndex + 1}: field "password" is required`);
    }

    let provider = row.provider;

    if (!provider) {
      console.log(`Provider not given. Defaulting to "gmail".`);
      provider = 'gmail';
    }

    provider = provider.toLowerCase();

    if (provider !== 'gmail' && provider !== 'microsoft') {
      if (!row.providerIMAPHost) {
        errors.push(
          `Row ${rowIndex + 1}: Provider IMAP Host in field 'providerIMAPHost' is required for non-Gmail providers`
        );
      }
      if (!row.providerIMAPPort) {
        errors.push(
          `Row ${rowIndex + 1}: Provider IMAP Port in field 'providerIMAPPort' is required for non-Gmail providers`
        );
      } else if (isNaN(Number(row.providerIMAPPort))) {
        errors.push(
          `Row ${rowIndex + 1}: Provider IMAP Port in field 'providerIMAPPort' must be a number`
        );
      }

      if (!row.providerSMTPHost) {
        errors.push(
          `Row ${rowIndex + 1}: SMTP Host in field 'providerSMTPHost' is required for non-Gmail providers. `
        );
      }
      if (!row.providerSMTPPort) {
        errors.push(
          `Row ${rowIndex + 1}: SMTP Port in field 'providerSMTPPort' is required for non-Gmail providers`
        );
      } else if (isNaN(Number(row.providerSMTPPort))) {
        errors.push(
          `Row ${rowIndex + 1}: SMTP Port in field 'providerSMTPPort' must be a number`
        );
      }

      if (!row.providerInboxName) {
        errors.push(
          `Row ${rowIndex + 1}: Inbox Name in 'providerInboxName' is required for non-Gmail providers`
        );
      }
      if (!row.providerSentBoxName) {
        errors.push(
          `Row ${rowIndex + 1}: Sent Box Name in 'providerSentBoxName' is required for non-Gmail providers`
        );
      }
    }

    if (errors.length > 0) {
      console.log(`${errors.length} Errors found. Errors: `);
      console.log(errors);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: `${errors.length} Errors found.`,
            notificationId: crypto.randomUUID(),
            severity: 'error',
            type: 'alert',
          },
          ...errors.map((error) => {
            return {
              message: error,
              notificationId: crypto.randomUUID(),
              severity: 'error',
              type: 'alert',
            } satisfies SnackBarEntry;
          }),
        ],
      });
      return { valid: false, errors };
    }

    try {
      const validatedData: ValidatedEmailCredential = {
        imapEmail: row.emailId,
        imapPassword: row.password,
        meetingLink: row.meetingLink || '',
        provider,
        providerIMAPHost: row.providerIMAPHost || 'imap.gmail.com',
        providerIMAPPort: Number(row.providerIMAPPort || 993),
        providerSMTPHost: row.providerSMTPHost || 'smtp.gmail.com',
        providerSMTPPort: Number(row.providerSMTPPort || 587),
        providerInboxName: row.providerInboxName || 'INBOX',
        providerSentBoxName: row.providerSentBoxName || '[Gmail]/Sent Mail',
      };

      return { valid: true, errors: [], data: validatedData };
    } catch (error) {
      if (error instanceof Error) {
        return {
          valid: false,
          errors: [`Row ${rowIndex + 1}: ${error.message}`],
        };
      }
      return {
        valid: false,
        errors: [`Row ${rowIndex + 1}: Unknown error occurred`],
      };
    }
  }

  async function parseAndValidateEmailCredentialsCSV(
    file: File
  ): Promise<ValidationResult> {
    return new Promise((resolve) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results) => {
          const rows = results.data as EmailCredentialsCSVRow[];
          const allErrors: string[] = [];
          const validatedData: ValidatedEmailCredential[] = [];

          rows.forEach((row, index) => {
            const result = validateRow(row, index);

            if (!result.valid) {
              allErrors.push(...result.errors);
            } else if (!!result.data) {
              validatedData.push(result.data);
            }
          });

          if (results.errors.length > 0) {
            results.errors.forEach((error) => {
              allErrors.push(
                `CSV Parsing Error - Row ${error.row}: ${error.message}`
              );
            });
          }

          resolve({
            valid: allErrors.length === 0,
            errors: allErrors,
            data: validatedData,
          });
        },
        error: (error) => {
          resolve({
            valid: false,
            errors: [`Failed to parse CSV: ${error.message}`],
            data: [],
          });
        },
      });
    });
  }

  const handleCSVUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (!file || file.type !== 'text/csv') {
      return {
        success: false,
        message: 'Please upload a valid CSV file.',
      };
    }

    setParsing(true);
    try {
      const validationResult = await parseAndValidateEmailCredentialsCSV(file);

      if (!validationResult.valid) {
        return {
          success: false,
          message: `Validation failed with ${validationResult.errors.length} errors:\n${validationResult.errors.join('\n')}`,
        };
      }

      if (validationResult.data.length === 0) {
        return {
          success: false,
          message: 'No valid data found in the CSV file.',
        };
      }

      validationResult.data.map((item) => {
        setEmailsAdded((prev) => [...prev, item.imapEmail]);
      });
      setCredentials(validationResult.data);

      // setParsing(false);

      // const response = await fetchWithRewrites('/api/v1/inbox/add', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({ credentials: validationResult.data }),
      // });

      // const data = await response.json();

      // console.log('TEH DATA', data);

      setAllowUploading(true);
      return {
        success: true,
        message: `Successfully uploaded ${validationResult.data.length} email credentials.`,
        data: validationResult.data,
      };
    } catch (error) {
      return {
        success: false,
        message: `Error processing CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  };

  const handleDeleteEmail = (index: number) => {
    console.log('DELETE EMAIL', index);
  };

  const handleAddEmailConfig = async () => {
    setAddyingInbox(true);
    try {
      const response = await fetchWithRewrites('/api/v1/inbox/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credentials }),
      });

      const data = await response.json();
    } catch (error) {
      console.error('Error', error);
    } finally {
      setAddyingInbox(false);
      setParsing(false);
    }
  };
  return (
    <Modal
      aria-describedby="imap-modal-description"
      aria-labelledby="imap-modal-title"
      disableEscapeKeyDown={inboxEmails.length === 0}
      onClose={onClose}
      open={open}
    >
      <Box
        sx={{
          ...inboxCommonStyles.modalBox,
          width: 800,
          padding: 4,
          position: 'relative',
          height: '80vh',
          overflow: 'scroll',
        }}
      >
        <Typography
          id="imap-modal-title"
          sx={{
            marginBottom: 4,
          }}
          variant="h5"
        >
          Inbox Credentials
        </Typography>
        {inboxEmails.length > 0 && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="large"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <Close />
          </IconButton>
        )}

        {/* Existing Credentials Section */}
        {!parsing && existingCredentials.length > 0 && (
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="h6" gutterBottom>
              Current IMAP Configurations
            </Typography>
            {loadingExistingCredentials ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 3,
                }}
              >
                <List>
                  {existingCredentials.map((credential, index) => (
                    <ListItem
                      key={credential._id}
                      sx={{
                        borderBottom:
                          index < existingCredentials.length - 1
                            ? '1px solid'
                            : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <ListItemText
                        primary={credential.emailId}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Provider: {credential.provider || 'gmail'}
                            </Typography>
                            {credential.provider &&
                              credential.provider !== 'gmail' &&
                              credential.provider !== 'microsoft' && (
                                <>
                                  <Typography variant="caption" display="block">
                                    IMAP: {credential.providerIMAPHost || 'N/A'}
                                    :{credential.providerIMAPPort || 'N/A'}
                                  </Typography>
                                  <Typography variant="caption" display="block">
                                    SMTP: {credential.providerSMTPHost || 'N/A'}
                                    :{credential.providerSMTPPort || 'N/A'}
                                  </Typography>
                                </>
                              )}
                            {credential.meetingLink && (
                              <Typography variant="caption" display="block">
                                Meeting Link: {credential.meetingLink}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            <Typography variant="h6" gutterBottom>
              Add New IMAP Configuration
            </Typography>
          </Box>
        )}
        {parsing ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              {allowUploading
                ? 'Emails to be Added'
                : 'We encountered errors while processing your CSV. Please have a look at them.'}
            </Typography>
            {!allowUploading && (
              <Alert severity="info">
                <Box>
                  <Typography variant="caption">{`The following fields are mandatory: emailId, password, provider ('gmail', 'microsoft', or 'others').`}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption">{`In case it is 'others', you would also need these fields: 'providerImapHost', 'providerImapPort', 'providerSMTPHost', 'providerSMTPPort', 'providerInboxName', 'providerSentBoxName', 'meetingLink'.`}</Typography>
                </Box>
              </Alert>
            )}
            <Box
              sx={{
                maxHeight: '60vh', // Adjust this value based on your needs
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
              }}
            >
              <List>
                {emailsAdded.map((email, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => index}
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={email} />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Stack flexDirection="row" gap={2}>
              <Button
                color="error"
                fullWidth
                onClick={() => setParsing(false)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                color="primary"
                fullWidth
                onClick={handleAddEmailConfig}
                startIcon={addyingInbox ? <CircularProgress size={20} /> : null}
                variant="contained"
                disabled={!allowUploading}
              >
                Confirm
              </Button>
            </Stack>
          </Box>
        ) : (
          <Stack alignItems="center" spacing={2}>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              marginTop={2}
              width="100%"
            >
              <Select
                defaultValue="gmail"
                fullWidth
                onChange={handleProviderChange}
                placeholder="Select Provider"
                required
                value={emailSettings.provider}
                variant="outlined"
              >
                <MenuItem value="gmail">Gmail</MenuItem>
                <MenuItem value="microsoft">Microsoft</MenuItem>
                <MenuItem value="outlook">Outlook</MenuItem>
                <MenuItem value="yahoo">Yahoo</MenuItem>
                <MenuItem value="zoho">Zoho</MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </Select>

              <TextField
                error={
                  Boolean(emailSettings.error) &&
                  emailSettings.error.includes('email')
                }
                fullWidth
                helperText={
                  emailSettings.error.includes('email')
                    ? emailSettings.error
                    : ''
                }
                label="Email"
                onChange={handleInputChange('imapEmail')}
                required
                type="email"
                value={emailSettings.imapEmail}
                variant="outlined"
              />

              {emailSettings.provider !== 'microsoft' && (
                <TextField
                  fullWidth
                  label="App Password"
                  onChange={handleInputChange('imapPassword')}
                  required
                  type={showPassword ? 'text' : 'password'}
                  value={emailSettings.imapPassword}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <TextField
                error={
                  Boolean(emailSettings.error) &&
                  emailSettings.error.includes('meeting link')
                }
                fullWidth
                helperText={
                  emailSettings.error.includes('meeting link')
                    ? emailSettings.error
                    : ''
                }
                label="Meeting Link"
                onChange={handleInputChange('meetingLink')}
                required
                type="text"
                value={emailSettings.meetingLink}
                variant="outlined"
              />

              {emailSettings.provider === 'others' && (
                <Box display="flex" flexDirection="column" gap={2} width="100%">
                  <TextField
                    fullWidth
                    label="Provider IMAP Host"
                    onChange={handleInputChange('providerIMAPHost')}
                    required
                    type="text"
                    value={emailSettings.providerIMAPHost}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Provider IMAP Port"
                    onChange={handleInputChange('providerIMAPPort')}
                    required
                    type="number"
                    value={emailSettings.providerIMAPPort}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Provider SMTP Host"
                    onChange={handleInputChange('providerSMTPHost')}
                    required
                    type="text"
                    value={emailSettings.providerSMTPHost}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Provider SMTP Port"
                    onChange={handleInputChange('providerSMTPPort')}
                    required
                    type="number"
                    value={emailSettings.providerSMTPPort}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Provider Inbox Name"
                    onChange={handleInputChange('providerInboxName')}
                    required
                    type="text"
                    value={emailSettings.providerInboxName}
                    variant="outlined"
                  />
                  <TextField
                    fullWidth
                    label="Provider Sent Box Name"
                    onChange={handleInputChange('providerSentBoxName')}
                    required
                    type="text"
                    value={emailSettings.providerSentBoxName}
                    variant="outlined"
                  />
                </Box>
              )}

              {emailSettings.error && (
                <Typography color="error" variant="body2">
                  {emailSettings.error}
                </Typography>
              )}

              {emailSettings.provider === 'microsoft' ? (
                <Button
                  disabled={
                    emailSettings.loading ||
                    !emailSettings.imapEmail ||
                    !emailSettings.meetingLink
                  }
                  onClick={connectUsingMicrosoft}
                >
                  Connect Using Microsoft
                </Button>
              ) : (
                <Button
                  color="primary"
                  disabled={
                    emailSettings.loading ||
                    !emailSettings.imapEmail ||
                    !emailSettings.imapPassword ||
                    !emailSettings.meetingLink ||
                    Boolean(emailSettings.error) ||
                    (emailSettings.provider === 'others' &&
                      (!emailSettings.providerIMAPHost ||
                        !emailSettings.providerIMAPPort))
                  }
                  fullWidth
                  onClick={handleSave}
                  startIcon={
                    emailSettings.loading ? (
                      <CircularProgress size={20} />
                    ) : null
                  }
                  variant="contained"
                >
                  {emailSettings.loading ? 'Saving...' : 'Save'}
                </Button>
              )}
            </Box>
            <Typography>OR</Typography>
            <Box
              sx={{
                marginBottom: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                // alignItems: 'center',
                width: '100%',
                gap: 2,
              }}
            >
              <Typography variant="h6">Import emails from CSV</Typography>
              <Alert severity="info">
                <Box>
                  <Typography variant="caption">{`The following fields are mandatory: emailId, password, provider ('gmail', 'microsoft', or 'others').`}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption">{`In case it is 'others', you would also need these fields: 'providerImapHost', 'providerImapPort', 'providerSMTPHost', 'providerSMTPPort', 'providerInboxName', 'providerSentBoxName', 'meetingLink'.`}</Typography>
                </Box>
              </Alert>
              <Button variant="outlined" component="label" fullWidth>
                Upload CSV
                <input
                  hidden
                  accept=".csv"
                  type="file"
                  onChange={handleCSVUpload}
                />
              </Button>
            </Box>
          </Stack>
        )}
      </Box>
    </Modal>
  );
}

export default ImapModal;
